import React, { useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { X, Clock, MapPin, Video, ShieldCheck } from 'lucide-react';
import { Button } from './Button';
import { afspraakStijl } from './B2bAgenda';
import { B2B_SOORT_LABEL, type B2bAfspraak, type B2bLocatie, type B2bSoort } from '../services/lopendeTrajectenApi';

/** Formulier + detailpaneel bij de B2B-agenda; gedeeld door opdrachtgever en professional. */

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const LOCATIE_LABEL: Record<B2bLocatie, string> = { online: 'Online (BeLagom-videolink)', telefonisch: 'Telefonisch', op_locatie: 'Op locatie' };
/** Soort → logische standaardlocatie */
const SOORT_LOCATIE: Partial<Record<B2bSoort, B2bLocatie>> = { telefonisch_overleg: 'telefonisch', overleg_op_locatie: 'op_locatie', meeting: 'online' };

const lokaal = (datum: Date) => format(datum, "yyyy-MM-dd'T'HH:mm");

export interface NieuweAfspraakInvoer {
  tegenpartij_id: string;
  soort: B2bSoort;
  titel: string;
  omschrijving: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie_type: B2bLocatie;
}

export const B2bAfspraakFormulier: React.FC<{
  start: Date;
  tegenpartijLabel: string;
  tegenpartijen: Array<{ id: string; label: string }>;
  /** Wie moet hierna bevestigen — tekst onder het formulier */
  bevestigHint: string;
  bezig: boolean;
  onOpslaan: (invoer: NieuweAfspraakInvoer) => void;
  onSluiten: () => void;
}> = ({ start, tegenpartijLabel, tegenpartijen, bevestigHint, bezig, onOpslaan, onSluiten }) => {
  const [vorm, setVorm] = useState({
    tegenpartij_id: tegenpartijen[0]?.id ?? '',
    soort: 'meeting' as B2bSoort,
    titel: '',
    omschrijving: '',
    start: lokaal(start),
    eind: lokaal(new Date(start.getTime() + 60 * 60 * 1000)),
    locatie_type: 'online' as B2bLocatie,
  });
  const geldig = vorm.tegenpartij_id && vorm.titel.trim().length >= 3 && new Date(vorm.eind) > new Date(vorm.start);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onSluiten}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Nieuwe afspraak</h3>
          <button type="button" onClick={onSluiten} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {tegenpartijen.length === 0 ? (
            <p className="text-sm text-gray-600">Er is nog geen actieve samenwerking met vastgelegde afspraken (functie en tarief). Zonder tarief kan een afspraak niet worden gedeclareerd.</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input className={inputClass} value={vorm.titel} maxLength={255} onChange={(e) => setVorm({ ...vorm, titel: e.target.value })} placeholder="Bijv. Kwartaaloverleg verzuim" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tegenpartijLabel}</label>
                <select className={inputClass} value={vorm.tegenpartij_id} onChange={(e) => setVorm({ ...vorm, tegenpartij_id: e.target.value })}>
                  {tegenpartijen.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soort</label>
                  <select
                    className={inputClass}
                    value={vorm.soort}
                    onChange={(e) => {
                      const soort = e.target.value as B2bSoort;
                      setVorm({ ...vorm, soort, locatie_type: SOORT_LOCATIE[soort] ?? vorm.locatie_type });
                    }}
                  >
                    {(Object.keys(B2B_SOORT_LABEL) as B2bSoort[]).map((s) => (
                      <option key={s} value={s}>{B2B_SOORT_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Locatie</label>
                  <select className={inputClass} value={vorm.locatie_type} onChange={(e) => setVorm({ ...vorm, locatie_type: e.target.value as B2bLocatie })}>
                    {(Object.keys(LOCATIE_LABEL) as B2bLocatie[]).map((l) => (
                      <option key={l} value={l}>{LOCATIE_LABEL[l]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Begin</label>
                  <input type="datetime-local" className={inputClass} value={vorm.start} onChange={(e) => setVorm({ ...vorm, start: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Einde</label>
                  <input type="datetime-local" className={inputClass} value={vorm.eind} onChange={(e) => setVorm({ ...vorm, eind: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Omschrijving (optioneel)</label>
                <textarea className={inputClass} rows={3} maxLength={2000} value={vorm.omschrijving} onChange={(e) => setVorm({ ...vorm, omschrijving: e.target.value })} placeholder="Organisatorisch — geen namen of medische gegevens van medewerkers" />
              </div>
              <p className="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-md p-2">
                <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                {bevestigHint}
              </p>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <Button variant="outline" onClick={onSluiten}>Annuleren</Button>
          {tegenpartijen.length > 0 && (
            <Button
              disabled={!geldig}
              loading={bezig}
              onClick={() =>
                onOpslaan({
                  tegenpartij_id: vorm.tegenpartij_id,
                  soort: vorm.soort,
                  titel: vorm.titel.trim(),
                  omschrijving: vorm.omschrijving.trim() || null,
                  start_tijd: new Date(vorm.start).toISOString(),
                  eind_tijd: new Date(vorm.eind).toISOString(),
                  locatie_type: vorm.locatie_type,
                })
              }
            >
              Inplannen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

export const B2bAfspraakDetail: React.FC<{
  afspraak: B2bAfspraak;
  tegenpartij: string;
  /** Partij die naar dit paneel kijkt; bepaalt de bevestigingstekst */
  kijker: 'employer' | 'professional';
  onSluiten: () => void;
  children?: React.ReactNode;
}> = ({ afspraak: a, tegenpartij, kijker, onSluiten, children }) => {
  const stijl = afspraakStijl(a);
  const wachtOpMij = a.status === 'GEPLAND' && a.bevestiging_status === 'WACHT' && a.voorgesteld_door !== kijker;
  const ander = kijker === 'employer' ? 'de professional' : 'de opdrachtgever';
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <div>
          <h3 className="font-semibold text-gray-900">{a.titel}</h3>
          <p className="text-sm text-gray-600">{tegenpartij} · {B2B_SOORT_LABEL[a.soort] ?? a.soort}</p>
        </div>
        <button type="button" onClick={onSluiten} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <span className={`inline-block px-2 py-0.5 rounded border-l-4 text-xs font-medium ${stijl.klasse}`}>{stijl.label}</span>
        <div className="flex items-center gap-2 text-gray-700">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="capitalize">{format(new Date(a.start_tijd), 'EEEE d MMMM', { locale: nl })}</span>
          {format(new Date(a.start_tijd), 'HH:mm')}–{format(new Date(a.eind_tijd), 'HH:mm')} ({a.uren_gepland} u)
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          {a.locatie_type === 'op_locatie' ? <MapPin className="w-4 h-4 text-gray-400" /> : <Video className="w-4 h-4 text-gray-400" />}
          {LOCATIE_LABEL[a.locatie_type]}
          {a.video_link && a.status === 'GEPLAND' && a.bevestiging_status === 'BEVESTIGD' && (
            <a href={a.video_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Open videolink</a>
          )}
        </div>
        {a.omschrijving && <p className="text-gray-700 whitespace-pre-line">{a.omschrijving}</p>}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-700 bg-gray-50 rounded-md p-3">
          <dt className="text-gray-500">Uurtarief</dt>
          <dd>{a.uurtarief == null ? '—' : euro.format(a.uurtarief)}</dd>
          <dt className="text-gray-500">Uren geschreven</dt>
          <dd>{a.uren_geschreven == null ? '—' : `${a.uren_geschreven} u`}</dd>
          <dt className="text-gray-500">Bedrag</dt>
          <dd>{a.bedrag == null ? '—' : euro.format(a.bedrag)}</dd>
        </dl>
        {a.status === 'GEPLAND' && a.bevestiging_status === 'WACHT' && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
            {wachtOpMij
              ? `Ingepland door ${ander}. Zonder uw bevestiging telt deze afspraak niet mee voor de maandfactuur.`
              : `Wacht op bevestiging door ${ander}. Tot die tijd kunnen geen uren worden gedeclareerd.`}
          </p>
        )}
        {a.bevestiging_status === 'GEWEIGERD' && a.weigering_reden && <p className="text-xs text-gray-600">Reden weigering: {a.weigering_reden}</p>}
        {a.status === 'AFGEWEZEN' && a.afwijzing_reden && <p className="text-xs text-red-700">Uren afgewezen: {a.afwijzing_reden}</p>}
        {a.bevestigd_at && a.bevestiging_status === 'BEVESTIGD' && (
          <p className="text-xs text-gray-500">Wederzijds bevestigd op {format(new Date(a.bevestigd_at), 'd MMM yyyy HH:mm', { locale: nl })}</p>
        )}
        {children && <div className="flex flex-wrap gap-2 pt-1">{children}</div>}
      </div>
    </div>
  );
};
